import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { toast } from "sonner";

/**
 * EmailSubscribe — single-input opt-in for localized sales alerts.
 *
 * Compliance note (per founder, intentional): we collect EMAIL ONLY (and a
 * silent ZIP if the user already set one via LocationGate). Never a name,
 * DOB, phone, address, or anything tying identity to cannabis interest.
 * The unsubscribe path lives server-side via the `unsubscribed_at` flag —
 * every email we send will include a one-click unsubscribe link.
 */
export const EmailSubscribe = ({
  variant = "card", // "card" | "inline"
  source = "home",
  testId,
}) => {
  const { location } = useApp();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const v = email.trim();
    if (!v || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) {
      toast.error("Enter a valid email");
      return;
    }
    setBusy(true);
    try {
      await api.post("/subscriptions/email", {
        email: v,
        zip: location?.zip || null,
        source,
      });
      setDone(true);
      toast.success("You're on the list — sales alerts coming soon");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (variant === "inline") {
    return done ? (
      <p
        data-testid={testId || "email-subscribe-done"}
        className="text-xs text-emerald-400 flex items-center gap-1.5"
      >
        <Check className="w-3.5 h-3.5" /> Subscribed — watch your inbox.
      </p>
    ) : (
      <form
        onSubmit={submit}
        data-testid={testId || "email-subscribe-inline"}
        className="flex gap-2 items-center"
      >
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          disabled={busy}
          autoComplete="email"
          maxLength={200}
          className="h-9 bg-background/60 text-xs flex-1 min-w-0"
          data-testid="email-subscribe-input"
        />
        <Button
          type="submit"
          disabled={busy}
          data-testid="email-subscribe-submit"
          className="rounded-full h-9 px-4 text-xs bg-primary text-primary-foreground hover:bg-primary/90 shrink-0"
        >
          {busy ? "..." : "Notify me"}
        </Button>
      </form>
    );
  }

  // "card" variant — used on Home above the install/CTA section
  return (
    <section
      data-testid={testId || "email-subscribe-card"}
      className="container-narrow mt-12"
    >
      <div className="rounded-3xl border border-border bg-card p-8 sm:p-12 grid md:grid-cols-[1fr_auto] items-center gap-6">
        <div className="flex items-start gap-5">
          <div className="hidden sm:flex w-14 h-14 rounded-2xl bg-secondary text-foreground items-center justify-center shrink-0">
            <Mail className="w-6 h-6" strokeWidth={1.6} />
          </div>
          <div>
            <div className="eyebrow mb-2">Sales alerts</div>
            <h3 className="font-serif text-3xl">Get sales near you.</h3>
            <p className="text-sm text-foreground/70 mt-2 max-w-lg leading-relaxed">
              We&apos;ll send a quick note when shops near
              {location?.zip ? ` ${location.zip}` : " you"} drop new
              promotions. Email only — no name, no phone, never sold.
              Unsubscribe anytime in one click.
            </p>
          </div>
        </div>
        {done ? (
          <div
            data-testid="email-subscribe-card-done"
            className="inline-flex items-center gap-2 text-sm text-emerald-300"
          >
            <Check className="w-4 h-4" /> You&apos;re on the list.
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="flex flex-wrap gap-2 items-center w-full md:w-auto"
          >
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              disabled={busy}
              autoComplete="email"
              maxLength={200}
              data-testid="email-subscribe-card-input"
              className="h-11 min-w-[220px] flex-1 bg-background/60"
            />
            <Button
              type="submit"
              disabled={busy}
              data-testid="email-subscribe-card-submit"
              className="rounded-full h-11 px-6 bg-primary text-primary-foreground hover:bg-primary/90 shrink-0"
            >
              {busy ? "..." : "Notify me"}
            </Button>
          </form>
        )}
      </div>
    </section>
  );
};
